'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import { useState } from 'react';

import Image from '@eliadmin/core/src/components/image/image-wrapped';

import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { buttonVariants } from '@eliadmin/core/src/components/ui/button';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { CgMenuGridO } from 'react-icons/cg';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';

// Both responsive layouts share one first-image request at a given viewport.
const FIRST_IMAGE_SIZES = '(min-width: 768px) 733px, calc(100vw - 32px)';

export const RealtyGallery = ({ allImages }: { allImages: { url: string; name: string }[] }) => {
  const { t } = useTranslations();

  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  return (
    <>
      <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
      <Carousel
        className={{
          container: 'h-[280px] w-full sm:h-[350px]',
          main: 'h-[280px] w-full overflow-hidden rounded-lg sm:h-[350px]',
          slide: 'relative h-[280px] w-full overflow-hidden rounded-lg sm:h-[350px]',
          wrapper: 'block h-[280px] w-full sm:h-[350px] md:hidden',
        }}
        showDots
        slides={allImages.map((image, index) => (
          <Image
            key={image.url}
            src={image.url}
            alt={resolveImageAlt(image, undefined)}
            fill
            className="object-cover object-center"
            loading={index === 0 ? 'eager' : 'lazy'}
            fetchPriority={index === 0 ? 'high' : 'auto'}
            sizes={index === 0 ? FIRST_IMAGE_SIZES : '(max-width: 767px) calc(100vw - 32px), 360px'}
          />
        ))}
      />
      <div className={cn('hidden w-full grid-cols-2 gap-4 md:grid')}>
        {allImages.slice(0, 6).map((image, index) => {
          const isLarge = index === 0 || index === 3;

          return (
            <button
              type="button"
              onClick={handleOpenGallery(allImages, index)}
              key={image.url}
              aria-label={`${t('text1401')}: ${image.name}`}
              className={cn(
                'relative w-full cursor-pointer appearance-none overflow-hidden rounded-lg border-0 bg-transparent p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                isLarge ? 'col-span-2 h-[400px] lg:h-[577px]' : 'col-span-1 h-[220px] lg:h-[280px]',
              )}
            >
              {index === 5 && ShowAllButton(t('text1401'))}
              <Image
                src={image?.url}
                alt={resolveImageAlt(image, undefined)}
                fill
                loading={index === 0 ? 'eager' : 'lazy'}
                fetchPriority={index === 0 ? 'high' : 'auto'}
                className="object-cover object-center"
                sizes={index === 0 ? FIRST_IMAGE_SIZES : isLarge ? '733px' : '360px'}
              />
            </button>
          );
        })}
      </div>
    </>
  );
};

const ShowAllButton = (label: string) => {
  return (
    <span
      aria-hidden="true"
      className={cn(
        buttonVariants({ variant: 'secondary' }),
        'absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap uppercase',
      )}
    >
      <CgMenuGridO size={24} /> {label}
    </span>
  );
};
